<template>
  <b-card no-body class="item-status-section">
    <div class="d-flex flex-wrap justify-content-between align-items-center px-2 pt-2 pb-1">
      <div>
        <h4 class="mb-0">
          PR Item Status
        </h4>
        <small class="text-muted">
          Status pemenuhan item di semua Purchase Request
        </small>
      </div>

      <div class="d-flex align-items-center">
        <b-dropdown
          text="Export"
          variant="outline-secondary"
          size="sm"
          class="mr-1"
          right
        >
          <template #button-content>
            <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
            Export
          </template>
          <b-dropdown-item :disabled="exporting" @click="onExport('shown')">
            <feather-icon icon="EyeIcon" size="14" class="mr-50" />
            Export yang tampil ({{ items.length }} baris)
          </b-dropdown-item>
          <b-dropdown-item :disabled="exporting" @click="onExport('all')">
            <feather-icon icon="DatabaseIcon" size="14" class="mr-50" />
            Export semua ({{ totalRows }} baris sesuai filter)
          </b-dropdown-item>
        </b-dropdown>

        <b-button-group size="sm">
          <b-button
            :variant="viewMode === 'card' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'card'"
          >
            <feather-icon icon="GridIcon" size="14" class="mr-50" />
            Card
          </b-button>
          <b-button
            :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
            @click="viewMode = 'table'"
          >
            <feather-icon icon="ListIcon" size="14" class="mr-50" />
            Table
          </b-button>
        </b-button-group>
      </div>
    </div>

    <!-- Legend -->
    <div class="item-status-legend px-2 pb-1">
      <span
        v-for="option in statusOptions.filter(o => o.value)"
        :key="option.value"
        class="item-status-legend-item"
      >
        <span class="item-status-legend-dot" :class="`bg-${option.activeVariant}`" />
        <span class="item-status-legend-label">{{ option.label }}</span>
        <span class="text-muted item-status-legend-desc">— {{ option.description }}</span>
      </span>
    </div>

    <div class="px-2 pb-2">
      <b-row>
        <b-col
          md="4"
          lg="3"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center item-status-per-page">
            <label class="mb-0 mr-50">Show</label>
            <v-select
              v-model="perPage"
              :options="perPageOptions"
              :clearable="false"
              class="item-status-per-page-select"
              @input="onPerPageChange"
            />
            <label class="mb-0 ml-50">entries</label>
          </div>
        </b-col>

        <b-col
          md="4"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <div class="item-status-search">
            <feather-icon icon="SearchIcon" size="16" class="item-status-search-icon" />
            <b-form-input
              v-model="searchQuery"
              placeholder="Cari nama atau kode item..."
              @input="onSearchInput"
            />
          </div>
        </b-col>

        <b-col
          md="4"
          lg="4"
        >
          <v-select
            v-model="statusFilter"
            :options="statusOptions"
            :reduce="option => option.value"
            label="label"
            placeholder="Filter status"
            :clearable="false"
            class="item-status-filter-select"
            @input="onStatusFilterChange"
          >
            <template #option="option">
              <span class="item-status-legend-dot" :class="`bg-${option.activeVariant}`" />
              {{ option.label }}
            </template>
            <template #selected-option="option">
              <span class="item-status-legend-dot" :class="`bg-${option.activeVariant}`" />
              {{ option.label }}
            </template>
          </v-select>
        </b-col>
      </b-row>
    </div>

    <b-overlay :show="loading" rounded="sm">
      <div v-if="items.length > 0">
        <!-- Card view -->
        <b-row v-if="viewMode === 'card'" class="match-height px-2">
          <b-col
            v-for="(row, index) in items"
            :key="row.purchase_request_detail_id"
            md="6"
            lg="4"
          >
            <b-card
              class="item-status-card mb-1"
              :class="`item-status-card--${statusVariant(row.status)}`"
              no-body
            >
              <div class="p-1">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="item-status-icon">
                    <feather-icon icon="PackageIcon" size="18" />
                  </div>
                  <b-badge pill :variant="statusVariant(row.status)">
                    {{ row.status }}
                  </b-badge>
                </div>

                <h6 class="mb-0 text-truncate" :title="row.item_name">
                  {{ row.item_name || '-' }}
                </h6>
                <small class="text-muted">{{ row.item_code || '-' }}</small>

                <b-row class="mt-1 item-status-meta">
                  <b-col cols="6">
                    <small class="text-muted d-block">Cabang</small>
                    <span>{{ row.branch_name || '-' }}</span>
                  </b-col>
                  <b-col cols="6">
                    <small class="text-muted d-block">Dari PR</small>
                    <span>{{ row.purchase_request_number || '-' }}</span>
                  </b-col>
                  <b-col cols="6" class="mt-1">
                    <small class="text-muted d-block">Qty Diminta</small>
                    <span>{{ row.qty_requested }}</span>
                  </b-col>
                  <b-col cols="6" class="mt-1">
                    <small class="text-muted d-block">Qty Disuplai</small>
                    <span>{{ row.qty_supplied }}</span>
                  </b-col>
                </b-row>

                <span class="text-muted item-status-no">No. {{ (currentPage - 1) * perPage + index + 1 }}</span>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!-- Table view -->
        <b-table
          v-else
          :items="tableItems"
          :fields="tableFields"
          responsive
          class="mb-0 mx-2"
          style="width: calc(100% - 2rem)"
        >
          <template #cell(status)="data">
            <b-badge pill :variant="statusVariant(data.value)">
              {{ data.value }}
            </b-badge>
          </template>
        </b-table>
      </div>

      <div v-else-if="!loading" class="text-center text-muted py-3">
        Tidak ada item ditemukan.
      </div>

      <app-pagination-footer
        v-if="totalRows > perPage"
        :meta="paginationMeta"
        :current-page="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        @update:currentPage="onPageChange"
      />
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BBadge,
  BOverlay,
  BFormInput,
  BButton,
  BButtonGroup,
  BTable,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import axios from 'axios'
import { saveAs } from 'file-saver'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import apiEndpoints from '@/constants/apiEndpoints'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BBadge,
    BOverlay,
    BFormInput,
    BButton,
    BButtonGroup,
    BTable,
    BDropdown,
    BDropdownItem,
    vSelect,
    AppPaginationFooter,
  },
  data() {
    return {
      loading: false,
      exporting: false,
      items: [],
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100],
      totalRows: 0,
      searchQuery: '',
      searchDebounce: null,
      viewMode: 'card',
      statusFilter: '',
      statusOptions: [
        {
          label: 'Semua',
          value: '',
          activeVariant: 'primary',
          description: 'Tampilkan semua status',
        },
        {
          label: 'Pending',
          value: 'Pending',
          activeVariant: 'secondary',
          description: 'Belum dibuatkan PO',
        },
        {
          label: 'Open',
          value: 'Open',
          activeVariant: 'dark',
          description: 'Sudah dibuatkan PO',
        },
        {
          label: 'On Purchasing',
          value: 'On Purchasing',
          activeVariant: 'warning',
          description: 'Semua qty sudah masuk purchasing',
        },
        {
          label: 'Partial',
          value: 'Partial',
          activeVariant: 'info',
          description: 'Sebagian sudah lewat konfirmasi warehouse',
        },
        {
          label: 'Bought',
          value: 'Bought',
          activeVariant: 'success',
          description: 'Semua qty sudah lewat konfirmasi warehouse',
        },
      ],
      tableFields: [
        { key: 'no', label: 'No' },
        { key: 'item_code', label: 'Item Code' },
        { key: 'item_name', label: 'Nama Item' },
        { key: 'purchase_request_type', label: 'Type' },
        { key: 'purchase_request_category', label: 'Category' },
        { key: 'branch_name', label: 'Cabang' },
        { key: 'purchase_request_number', label: 'Dari PR' },
        { key: 'qty_requested', label: 'Qty Diminta' },
        { key: 'qty_supplied', label: 'Qty Disuplai' },
        { key: 'status', label: 'Status' },
      ],
    }
  },
  computed: {
    paginationMeta() {
      const from = this.totalRows === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1
      const to = Math.min(this.currentPage * this.perPage, this.totalRows)
      return { from, to, of: this.totalRows }
    },
    tableItems() {
      return this.items.map((row, index) => ({
        ...row,
        no: (this.currentPage - 1) * this.perPage + index + 1,
      }))
    },
  },
  mounted() {
    this.getItemStatusList()
  },
  methods: {
    statusVariant(status) {
      if (status === 'Bought') return 'success'
      if (status === 'Partial') return 'info'
      if (status === 'On Purchasing') return 'warning'
      if (status === 'Open') return 'dark'
      return 'secondary'
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1
        this.getItemStatusList()
      }, 400)
    },
    onStatusFilterChange(value) {
      this.statusFilter = value
      this.currentPage = 1
      this.getItemStatusList()
    },
    onPageChange(page) {
      this.currentPage = page
      this.getItemStatusList()
    },
    onPerPageChange(value) {
      this.perPage = value
      this.currentPage = 1
      this.getItemStatusList()
    },
    onExport(scope) {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }

      this.exporting = true

      axios
        .get(
          `${process.env.VUE_APP_API_URL}${apiEndpoints.API_EXPORT_PURCHASE_REQUEST_ITEM_STATUS_LIST}`,
          {
            headers,
            responseType: 'arraybuffer',
            params: {
              scope,
              limit: scope === 'shown' ? this.perPage : undefined,
              offset: scope === 'shown' ? (this.currentPage - 1) * this.perPage : undefined,
              search: this.searchQuery || undefined,
              status: this.statusFilter || undefined,
            },
          },
        )
        .then(response => {
          const filename = `pr_item_status_${scope}.xlsx`
          const blob = new Blob([response.data], { type: response.headers['content-type'] })
          saveAs(blob, filename)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Export berhasil',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Export gagal',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.exporting = false
        })
    },
    getItemStatusList() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }

      this.loading = true

      axios
        .get(
          `${process.env.VUE_APP_API_URL}${apiEndpoints.API_GET_PURCHASE_REQUEST_ITEM_STATUS_LIST}`,
          {
            headers,
            params: {
              limit: this.perPage,
              offset: (this.currentPage - 1) * this.perPage,
              search: this.searchQuery || undefined,
              status: this.statusFilter || undefined,
            },
          },
        )
        .then(response => {
          if (response.data.success === true) {
            this.items = response.data.data || []
            this.totalRows = response.data.data_count || 0
          } else {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Get Item Status Failed',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          }
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Get Item Status Failed',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.item-status-legend {
  display: flex;
  flex-wrap: wrap;
  gap: .25rem 1.25rem;

  &-item {
    display: inline-flex;
    align-items: center;
    font-size: .8rem;
    white-space: nowrap;
  }

  &-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: .4rem;
    flex-shrink: 0;
  }

  &-label {
    font-weight: 600;
    margin-right: .3rem;
  }

  &-desc {
    font-size: .75rem;
  }
}

.item-status-search {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #fff;
  border: 1px solid #d8d6de;
  border-radius: .357rem;
  transition: box-shadow .15s ease, border-color .15s ease;

  &-icon {
    margin-left: .85rem;
    color: #b9b9c3;
    flex-shrink: 0;
  }

  ::v-deep .form-control {
    border: none;
    box-shadow: none !important;
    background: transparent;

    &:focus {
      border: none;
      box-shadow: none !important;
      background: transparent;
    }
  }

  &:focus-within {
    border-color: #7367f0;
    box-shadow: 0 3px 10px 0 rgba(115, 103, 240, .15);
  }
}

.item-status-filter-select {
  ::v-deep .vs__dropdown-toggle {
    border-radius: .357rem;
    border-color: #d8d6de;
    background-color: #fff;
    padding: .1rem 0;
  }

  ::v-deep .vs__selected {
    display: flex;
    align-items: center;
  }
}

.item-status-per-page {
  min-height: 38px;
  white-space: nowrap;
}

.item-status-per-page-select {
  width: 82px;
  flex: 0 0 82px;

  ::v-deep .vs__dropdown-toggle {
    min-height: 38px;
    border-radius: .357rem;
    border-color: #d8d6de;
    background-color: #fff;
  }
}

.item-status-card {
  height: 100%;
  border-radius: .75rem;
  border: 1px solid rgba(34, 41, 47, .08);
  box-shadow: 0 2px 6px 0 rgba(34, 41, 47, .06);
  transition: transform .15s ease, box-shadow .15s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px 0 rgba(34, 41, 47, .12);
  }

  &--success .item-status-icon {
    background-color: rgba(40, 199, 111, .12);
    color: #28c76f;
  }

  &--info .item-status-icon {
    background-color: rgba(0, 207, 232, .12);
    color: #00cfe8;
  }

  &--secondary .item-status-icon {
    background-color: rgba(130, 134, 139, .12);
    color: #82868b;
  }

  &--warning .item-status-icon {
    background-color: rgba(255, 159, 67, .12);
    color: #ff9f43;
  }

  &--dark .item-status-icon {
    background-color: rgba(52, 58, 64, .12);
    color: #343a40;
  }
}

.item-status-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.item-status-meta {
  border-top: 1px dashed rgba(34, 41, 47, .1);
  padding-top: .75rem;
  margin-top: .75rem;
}

.item-status-no {
  display: block;
  text-align: right;
  font-size: .7rem;
  margin-top: .5rem;
}
</style>
